import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import axios from '../../../../../config/axios'
import styled from 'styled-components'

import { Eye, Pencil, Trash2 } from 'lucide-react'

import { EquipmentOutletType } from '../../index'

import ViewVehicleModal from '../ViewVehicleModal/index'
import EditVehicleModal from '../EditVehicleModal/index'
import { FeedbackContext } from '../../../../../contexts/FeedbackContext'
import ConfirmationModal from '../../../../../components/ModalConfirmation'
import {
  StandardDataTable,
  useColumnsState,
  StandardColumn,
} from '../../../../../components/StandardPage'

const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/equipment`

const vehiclesScreenKey = 'equipment_vehicles'

interface BackendVehicleListItem {
  id: string
  plate: string
  model: string
  year: number
  capacity: string
  bodyType: string
  restriction: string | null
  periodicMaintenances: Array<{
    id?: number
    maintenanceName: string
    periodicityValue: number
    periodicityUnit: string
  }>
}

type ExistingVehicleData = BackendVehicleListItem

const TableRoot = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
`

const InfoMessage = styled.div`
  padding: 40px;
  text-align: center;
  color: #8c98a4;
  font-size: 16px;
`

const buildVehiclesColumns = (): StandardColumn<BackendVehicleListItem>[] => [
  { id: 'plate', label: 'Placa', accessor: (vehicle) => vehicle.plate },
  { id: 'model', label: 'Modelo', accessor: (vehicle) => vehicle.model },
  { id: 'year', label: 'Ano', accessor: (vehicle) => vehicle.year },
  { id: 'capacity', label: 'Capacidade', accessor: (vehicle) => vehicle.capacity },
  { id: 'restriction', label: 'Restrição', accessor: (vehicle) => vehicle.restriction || 'N/A' },
]

const VehicleTable: React.FC = () => {
  const { refresh, search, appliedFilters } =
    useOutletContext<EquipmentOutletType>()
  const navigate = useNavigate()

  const [vehicles, setVehicles] = useState<BackendVehicleListItem[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const [showViewModal, setShowViewModal] = useState<boolean>(false)
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(
    null,
  )

  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [vehicleToEdit, setVehicleToEdit] =
    useState<ExistingVehicleData | null>(null)

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [vehicleToDelete, setVehicleToDelete] = useState<string | null>(null)

  const { setFeedback } = useContext(FeedbackContext)

  const columns = useMemo(() => buildVehiclesColumns(), [])
  const { columnsState, handleColumnsChange, handleRestoreColumns } =
    useColumnsState(vehiclesScreenKey, columns)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    const token = localStorage.getItem('token')

    if (!token) {
      setError('Sessão expirada. Por favor, faça login novamente.')
      setLoading(false)
      navigate('/login')
      return
    }

    try {
      const params: any = {}
      if (search && search.trim() !== '') {
        params.search = search
      }
      if (appliedFilters && appliedFilters.length > 0) {
        params.filters = JSON.stringify(appliedFilters)
      }

      const response = await axios.get<any[]>(`${API_BASE_URL}/vehicles`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      })

      const fetchedVehicles: BackendVehicleListItem[] = response.data.map(
        (item) => ({
          id: String(item.id),
          plate: item.plate,
          model: item.model,
          year: item.year,
          capacity: item.capacity || '',
          bodyType: item.bodyType || '',
          restriction: item.restriction === undefined ? null : item.restriction,
          periodicMaintenances: item.periodicMaintenances || [],
        }),
      )

      setVehicles(fetchedVehicles)
    } catch (err) {
      console.error('Erro ao buscar dados dos veículos:', err)
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError('Não autorizado. Por favor, faça login novamente.')
        navigate('/login')
      } else {
        setError('Não foi possível carregar os veículos. Tente novamente.')
      }
    } finally {
      setLoading(false)
    }
  }, [search, appliedFilters, refresh, navigate])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleViewDetails = (vehicleId: string) => {
    setSelectedVehicleId(vehicleId)
    setShowViewModal(true)
  }

  const handleCloseViewModal = () => {
    setShowViewModal(false)
    setSelectedVehicleId(null)
  }

  const handleEdit = (vehicle: BackendVehicleListItem) => {
    setVehicleToEdit(vehicle)
    setIsEditModalOpen(true)
  }

  const handleEditFromViewModal = (vehicle: any) => {
    const convertedVehicle: BackendVehicleListItem = {
      ...vehicle,
      capacity: vehicle.capacity?.toString() || '0'
    }
    setShowViewModal(false)
    setSelectedVehicleId(null)
    setVehicleToEdit(convertedVehicle)
    setIsEditModalOpen(true)
  }

  const handleSaveEditVehicleModal = async (
    vehicleId: string,
    updatedData: any,
  ): Promise<boolean> => {
    const token = localStorage.getItem('token')
    if (!token) {
      setFeedback({
        isOpen: true,
        message: 'Sessão expirada. Por favor, faça login novamente.',
        statusOk: false,
      })
      navigate('/login')
      return false
    }

    try {
      const response = await axios.patch(
        `${API_BASE_URL}/vehicles/${vehicleId}`,
        updatedData,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.status === 200) {
        setFeedback({
          isOpen: true,
          message: 'Veículo atualizado com sucesso!',
          statusOk: true,
        })
        setIsEditModalOpen(false)
        setVehicleToEdit(null)
        fetchData()
        return true
      } else {
        setFeedback({
          isOpen: true,
          message: `Erro ao atualizar veículo: ${response.statusText || 'Resposta inesperada.'}`,
          statusOk: false,
        })
        return false
      }
    } catch (err) {
      console.error('Erro ao atualizar veículo:', err)
      let errorMessage = 'Ocorreu um erro ao atualizar o veículo.'
      if (axios.isAxiosError(err)) {
        errorMessage = err.response?.data?.message || err.message
        if (err.response?.status === 401) {
          setFeedback({
            isOpen: true,
            message: 'Não autorizado. Por favor, faça login novamente.',
            statusOk: false,
          })
          navigate('/login')
          return false
        } else if (err.response?.status === 404) {
          errorMessage = 'Veículo não encontrado.'
        } else if (err.response?.status === 400) {
          errorMessage = `Dados inválidos: ${err.response?.data?.details ||
            err.response?.data?.message ||
            'Verifique os campos preenchidos.'
            }`
        }
      }
      setFeedback({
        isOpen: true,
        message: `Falha na atualização: ${errorMessage}`,
        statusOk: false,
      })
      return false
    }
  }

  const handleDeleteClick = (vehicleId: string) => {
    setVehicleToDelete(vehicleId)
    setShowDeleteModal(true)
  }

  const handleConfirmDelete = async () => {
    if (vehicleToDelete === null) return
    const vehicleId = vehicleToDelete
    setShowDeleteModal(false)
    setVehicleToDelete(null)

    const token = localStorage.getItem('token')
    if (!token) {
      setFeedback({
        isOpen: true,
        message: 'Sessão expirada. Por favor, faça login novamente.',
        statusOk: false,
      })
      navigate('/login')
      return
    }

    try {
      setLoading(true)
      await axios.delete(`${API_BASE_URL}/vehicles/${vehicleId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      setVehicles((prevVehicles) =>
        prevVehicles.filter((vehicle) => vehicle.id !== vehicleId),
      )
      setFeedback({
        isOpen: true,
        message: 'Veículo excluído com sucesso!',
        statusOk: true,
      })
    } catch (err) {
      console.error(`Erro ao excluir veículo ${vehicleId}:`, err)
      let errorMessage = 'Falha ao excluir o veículo. Tente novamente.'
      if (axios.isAxiosError(err)) {
        const backendMessage = err.response?.data?.message || ''
        if (
          backendMessage.includes('constraint') ||
          backendMessage.includes('foreign key') ||
          backendMessage.includes('fkey') ||
          backendMessage.includes('foreign-key')
        ) {
          errorMessage = 'O veículo não pode ser excluído pois possui manutenções ou serviços vinculados a ele.'
        } else if (err.response?.status === 401) {
          setFeedback({
            isOpen: true,
            message: 'Não autorizado para excluir. Por favor, faça login novamente.',
            statusOk: false,
          })
          navigate('/login')
          return
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message
        }
      }
      setFeedback({
        isOpen: true,
        message: errorMessage,
        statusOk: false,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteModal(false)
    setVehicleToDelete(null)
  }

  if (error) {
    return <InfoMessage style={{ color: 'red' }}>{error}</InfoMessage>
  }

  return (
    <TableRoot>
      <StandardDataTable
        columns={columns}
        data={vehicles}
        columnsState={columnsState}
        onColumnsChange={handleColumnsChange}
        onRestoreColumns={handleRestoreColumns}
        isLoading={loading}
        exportFileName='veiculos'
        emptyMessage='Nenhum veículo encontrado.'
        rowActions={(vehicle) => [
          { label: 'Visualizar', icon: <Eye size={16} />, onClick: () => handleViewDetails(vehicle.id) },
          { label: 'Editar', icon: <Pencil size={16} />, onClick: () => handleEdit(vehicle) },
          { label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => handleDeleteClick(vehicle.id) },
        ]}
      />

      {showViewModal && selectedVehicleId !== null && (
        <ViewVehicleModal
          vehicleId={selectedVehicleId}
          onClose={handleCloseViewModal}
          onEdit={handleEditFromViewModal}
        />
      )}

      {isEditModalOpen && vehicleToEdit && (
        <EditVehicleModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false)
            setVehicleToEdit(null)
            fetchData()
          }}
          vehicleId={vehicleToEdit.id}
          initialVehicleData={vehicleToEdit}
          onSave={handleSaveEditVehicleModal}
        />
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir Veículo"
        message={`Tem certeza que deseja excluir o veículo com placa/ID: ${vehicleToDelete}? Esta ação não pode ser desfeita.`}
        actions={[
          {
            label: 'Cancelar',
            onClick: handleCancelDelete,
            variant: 'outline'
          },
          {
            label: loading ? 'Excluindo...' : 'Excluir',
            onClick: handleConfirmDelete,
            variant: 'danger'
          }
        ]}
      />
    </TableRoot>
  )
}

export default VehicleTable
